import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import type { ServerStatsRow } from './types';

export const monitoringKeys = {
  all: ['monitoring'] as const,
  latest: ['monitoring', 'latest'] as const,
  history: (serverName: string, from: string, to: string) =>
    ['monitoring', 'history', serverName, from, to] as const,
  allRange: (from: string, to: string) =>
    ['monitoring', 'all-range', from, to] as const,
};

/** GET /api/monitoring/servers/latest — current snapshot of every server.
 *  Polls every 60s while the tab is visible so the dashboard stays live. */
export function useLatestServers(refetchIntervalMs: number = 60_000) {
  return useQuery({
    queryKey: monitoringKeys.latest,
    queryFn: () =>
      api.get<ServerStatsRow[]>('/api/monitoring/servers/latest'),
    staleTime: 15_000,
    refetchInterval: refetchIntervalMs,
    refetchIntervalInBackground: false,
    select: (d) => ensureArray<ServerStatsRow>(d),
  });
}

/** GET /api/monitoring/servers/history — one server, date range. */
export function useServerHistory(
  serverName: string,
  fromDate: string,
  toDate: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: monitoringKeys.history(serverName, fromDate, toDate),
    queryFn: () =>
      api.get<ServerStatsRow[]>(
        `/api/monitoring/servers/history?serverName=${encodeURIComponent(
          serverName,
        )}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(
          toDate,
        )}`,
      ),
    enabled: enabled && Boolean(serverName && fromDate && toDate),
    staleTime: 30_000,
    select: (d) => ensureArray<ServerStatsRow>(d),
  });
}

/** GET /api/monitoring/servers/all — every server, date range. */
export function useAllServersRange(
  fromDate: string,
  toDate: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: monitoringKeys.allRange(fromDate, toDate),
    queryFn: () =>
      api.get<ServerStatsRow[]>(
        `/api/monitoring/servers/all?fromDate=${encodeURIComponent(
          fromDate,
        )}&toDate=${encodeURIComponent(toDate)}`,
      ),
    enabled: enabled && Boolean(fromDate && toDate),
    staleTime: 30_000,
    select: (d) => ensureArray<ServerStatsRow>(d),
  });
}
